<template lang="pug">
  .bundle-opt(v-if="!isHiddenBundleOption")
    rhpa-child(:buyRequest="buyRequest" v-if="options.length === 1 && (bundleOption.values[0] || {}).canChangeQty")
    component(
      v-else-if="availableOptions.length > 1  && !shouldHideMassStorageOption"
      :label="bundleOption.title"
      :is="componentType"
      :code="inputCode"
      :options="availableOptions"
      :modelValue="selectedOption"
      @update:modelValue="select($event)"
    )
    add-shelf-button(
      v-if="isLastVisibleShelf && hasProgressiveShelves && canAddAnotherShelf"
      :remaining="MAX_SHELVES"
      @click="addShelf"
    )
    configuration(
      v-if="(product || {}).type === 'configurable' && buyRequest.qty > 0"
      :buyRequest="buyRequest"
      :bundleBuyRequest="bundleBuyRequest"
      :ignoreAttributes="attributeOverlapWithMain || []"
      :hiddenAttributes="hiddenAttributes"
      :disabledValues="disabledConfigAttrs"
      :grayedOutValues="grayedOutConfigAttrs"
      :key="buyRequest.productId+'-'+mainBundleProductId"
      :class="'config-bundle-'+bundleOption?.title?.toLowerCase()?.replace(/ /g,'-')"
    )
</template>
<script>
import { computed, defineAsyncComponent, toRef } from 'vue'
import { updateCaveMiniFeetsForCableChange } from 'Src/js/pages/product/rackbuilder/products/cave/utils/caveUtils'
import { useShelfVisibility, MAX_SHELVES } from './custom/useShelfVisibility'
import { findMostSimilarOption, getBundleOptions } from './bundle-utils/bundle-utils'
import { getSideOptionsForTheCave } from './bundle-utils/rules/cave-rules'
import { getSideOptionsForSlingerX, getSlingerXGrayedOutValues } from './bundle-utils/rules/slingerX-rules'
import { canAddHybridShelf } from './bundle-utils/rules/hybrid-rules'
import { useProductStore } from '@/js/stores/productStore'
import RhpaChild from '@/js/components/rhpa/rhpa-child.vue'
import { createBuyRequest, createBuyRequestAsync, getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import { computePriceSync } from '@/js/helpers/price-utils'
import stockStatus from '@/js/helpers/stockStatus'
import { findBundleProduct } from '@/js/pages/product/rackbuilder/utils/rackbuilder-util'
import { isEnabledCustomProduct } from '@/js/customProducts/registry'
import { MSUnit } from '@/js/pages/product/rackbuilder/utils/ms-utils/ms-constants'
import { MMSUnit } from '@/js/pages/product/rackbuilder/utils/mms-utils/mms-constants'
import { SLINGER_X_SIDE_SKUS } from '@/js/pages/product/rackbuilder/products/rack/functionalTrainer/FM/slinger-x/slingerXConstants'

export default {
  name       : 'BundleOption',
  components : { 
    RhpaChild, 
    AddShelfButton: defineAsyncComponent(() => import('@/js/components/rhpa/custom/add-shelf-button.vue')),
  },
  inject: {
    rackHeight: {
      from    : 'rackHeight',
      default : undefined,
    },
  },

  props: {
    buyRequest: {
      type     : Object,
      required : true,
    },
    bundleBuyRequest: {
      type     : Object,
      required : true,
    },
    rackBuyRequest: {
      type     : Object,
      required : false,
      default  : undefined,
    },
    hiddenAttributes: {
      type    : Object,
      default : () => ({}),
    },
  },

  setup(props) {
    const productStore = useProductStore()
    
    const getBundleOptionTitle = () => {
      const bundleProduct = productStore.products[props.bundleBuyRequest?.productId]
      
      return bundleProduct?.bundleOptions?.find(o => o.id === props.buyRequest?.metadata?.bundleOptionId)?.title ?? ''
    }

    const bundleOptionTitleRef = computed(getBundleOptionTitle)
    const { isShelf, shelfNumber, isHiddenShelf, isLastVisibleShelf, addShelf } = useShelfVisibility(toRef(props, 'bundleBuyRequest'), bundleOptionTitleRef)

    return {
      productStore,
      isShelf,
      shelfNumber,
      isHiddenShelf,
      isLastVisibleShelf,
      addShelf,
    }
  },

  data: () => ({
    options        : [],
    selectedOption : '',
    MAX_SHELVES,
  }),

  computed: {
    products() {
      return this.productStore.products
    },

    isHiddenMiniFeet() {
      if (!this.isTheCave) return false
      
      return this.bundleOption?.title?.toLowerCase()?.includes('mini feet')
    },

    isHiddenBundleOption() {
      if (this.isHiddenMiniFeet) return true
      if (this.hasProgressiveShelves && this.isHiddenShelf) return true

      // A shelf row whose rules left it nothing to offer is retracted rather than rendered as an empty slot. The availableOptions watcher has already zeroed its qty, so the row is out of the rig either way - this is what the shopper sees of that.
      return this.hasProgressiveShelves && this.isShelf && !this.availableOptions.length
    },

    // Cave and Hybrid Rig both reveal their five shelf rows one at a time; every other bundle shows whatever shelf options it has.
    hasProgressiveShelves() {
      return this.isTheCave || this.isHybridRig
    },

    // Cave's five rows are always all reachable; only Hybrid's divider tray takes enough height to cost the rig a socket. The button sits on the last visible row, so the row it would reveal is the next one up.
    canAddAnotherShelf() {
      return !this.isHybridRig || canAddHybridShelf(this.$rogue, this.bundleBuyRequest, (this.shelfNumber ?? 0) + 1)
    },

    isTheCave() {
      return isEnabledCustomProduct(this.$rogue, 'cave', this.$rogue.getProductById(this.bundleBuyRequest.productId))
    },

    // True for the rack bundle and for every unit cloned from the addon bundle, so each rig counts its own visible shelves.
    isHybridRig() {
      return isEnabledCustomProduct(this.$rogue, 'hybrid-rig', this.$rogue.getProductById(this.bundleBuyRequest.productId))
    },

    isSlingerXSide() {
      const bundleProduct = this.$rogue.getProductById(this.bundleBuyRequest.productId)

      return SLINGER_X_SIDE_SKUS.includes(bundleProduct?.sku)
    },

    isMonsterMassStorage() {
      return !!this.bundleProduct?.sku && MMSUnit.includes(this.bundleProduct.sku) && this.$rogue.featureFlags.isOn('enable-monster-mass-storage')
    },

    isMassStorage() {
      return !!this.bundleProduct?.sku && MSUnit.includes(this.bundleProduct.sku) && this.$rogue.featureFlags.isOn('enable-mass-storage')
    },

    shouldHideMassStorageOption() {
      if (!(this.isMonsterMassStorage || this.isMassStorage) && (this.$rogue.featureFlags.isOn('enable-monster-mass-storage') || this.$rogue.featureFlags.isOn('enable-mass-storage'))) {
        return false
      }

      const hiddenTitles = ['Add Storage Wheels', 'Storage Wheels (2 Pack)', 'Shared Hardware', 'Solo Hardware']

      return hiddenTitles.includes(this.bundleOption?.title)
    },

    inputCode() {
      return this.bundleOption?.title?.split('(')[0]?.trim()?.toLowerCase()?.replace(/[^a-z0-9]/g, '') || ''
    },

    componentType() {
      // Show thumbnails
      if (this.bundleOption.type === 'radio') {
        return 'Swatches'
      }

      if (this.options.length > 3 || this.options.some(opt => opt.label?.length >= 5)) {
        return 'RogueSelect'
      }

      return 'Swatches'
    },

    product() {
      return this.products[this.buyRequest.productId]
    },

    bundleProduct() {
      return this.products[this.bundleBuyRequest.productId]
    },

    freeProduct() {
      if (this.bundleOption.includedProductId) {
        return this.products[this.bundleOption.includedProductId]
      }

      return null
    },

    availableOptions() {
      const mainProduct = this.products[this.bundleBuyRequest.children[0].productId]
      if (!mainProduct) {
        return []
      }

      // Hide cable systems & attachments on each side for The Cave 2.0
      if (this.isTheCave) {
        const leftCableSelection = findBundleProduct(this.$rogue, this.bundleBuyRequest, 'left cable system', true)?.name?.toLowerCase()
        const rightCableSelection = findBundleProduct(this.$rogue, this.bundleBuyRequest, 'right cable system', true)?.name?.toLowerCase()
        const title = this.bundleOption.title?.toLowerCase() ?? ''

        const panelProduct = findBundleProduct(this.$rogue, this.bundleBuyRequest, 'side panel', true)
        const panelSide = panelProduct?.attributes?.side?.toLowerCase()
        const sidePanelWidth = panelProduct?.attributes?.mass_width
        const leftSide = panelSide === 'left'
        const rightSide = panelSide === 'right'
        
        const sideWidths = {
          leftWidth  : leftSide ? sidePanelWidth : undefined,
          rightWidth : rightSide ? sidePanelWidth : undefined,
        }

        // Gets the data for all cave shelf selections
        const shelfSelections = Object.fromEntries(Array.from({ length: MAX_SHELVES }, (_, i) => [
          `shelf${i + 1}`, findBundleProduct(this.$rogue, this.bundleBuyRequest, `shelf ${i + 1}`, true),
        ]))

        const seatSelection = findBundleProduct(this.$rogue, this.bundleBuyRequest, 'lat pulldown seat', true)

        const caveResult = getSideOptionsForTheCave(
          this.$rogue,
          this.inputCode,
          title,
          this.rackHeight,
          leftCableSelection,
          rightCableSelection,
          sideWidths,
          getBundleOptions(this.$rogue, this.bundleOption, this.bundleBuyRequest, this.options),
          shelfSelections,
          seatSelection
        )

        if (caveResult === false) {
          return []
        }

        if (Array.isArray(caveResult)) {
          return caveResult
        }
      }

      // Hide attachments on each side for Slinger X — mirrors the Cave pattern using each side's own buy request
      if (this.isSlingerXSide) {
        const title = this.bundleOption.title?.toLowerCase() ?? ''
        const slingerXSideResult = getSideOptionsForSlingerX(
          this.$rogue,
          title,
          this.bundleBuyRequest,
          getBundleOptions(this.$rogue, this.bundleOption, this.bundleBuyRequest, this.options)
        )

        if (slingerXSideResult === false) {
          return []
        }

        if (Array.isArray(slingerXSideResult)) {
          return slingerXSideResult
        }
      }

      // Hybrid side units: their single crossmember option is always bundleBuyRequest.children[0], which would otherwise match the shared "main product" shortcut below and skip real filtering entirely.
      if (this.rackBuyRequest) {
        return getBundleOptions(this.$rogue, this.bundleOption, this.bundleBuyRequest, this.options, this.rackBuyRequest)
      }

      if (mainProduct === this.product || (this.bundleOption.values.length === 1 && this.bundleOption.isRequired)) {
        return this.options
      }

      return getBundleOptions(this.$rogue, this.bundleOption, this.bundleBuyRequest, this.options)
    },

    freeProductBuyRequest() {
      return this.bundleBuyRequest.children.find(c => c.metadata?.freeBundleOptionId === this.bundleOption.id)
    },

    bundleOption() {
      if (!this.buyRequest.metadata?.bundleOptionId) {
        this.$rogue.log.warn('Invalid bundle option buy request, no bundleOptionId', this.buyRequest)
      }
      const val = this.bundleProduct?.bundleOptions?.find(o => o.id === this.buyRequest.metadata?.bundleOptionId)
      if (!val) {
        this.$rogue.log.warn(`Unable to find bundle option for ${this.buyRequest.metadata?.bundleOptionId} on ${this.bundleProduct?.sku || 'NO PRODUCT'}. Valid ids: ${this.bundleProduct?.bundleOptions?.map(opt => opt.title)}`)
      }

      return val || { values: [] }
    },
    attributeOverlapWithMain() {
      // Check if the current buy request, and the main buy request, have similar attributes (like Height).
      // If so, we want to link them together - so if you change the main product, it changes this one
      const mainProduct = this.products[this.bundleBuyRequest.children[0].productId]

      if (!mainProduct || !this.product) {
        return null
      }

      // Make sure both this product and the main product are configurables
      if (this.product.type !== 'configurable' || mainProduct.type !== 'configurable' || mainProduct === this.product) {
        return null
      }

      const attrs = this.product.configurableAttributes
        ?.filter(attr => mainProduct.configurableAttributes?.some(a => a.code === attr.code))
        ?.map(attr => attr.code)

      return attrs?.length ? attrs : null
    },
    // Sometimes in bundles we need to disable specific options on a configurable... e.g. Rhino Rack Trainer, Hybrid Rig
    disabledConfigAttrs() {
      if (this.bundleBuyRequest.children[0] !== this.buyRequest) {
        return {}
      }

      // If they select Rhino Functional Trainer in the second bundle product,
      // and its a 4 post depth in the first (main) config, we need to disable the 30" and 43" depths
      const mainSimple = this.products[this.buyRequest.children?.[0].productId]

      // This only matters for 4 post options
      if (mainSimple?.attributes.rack_size?.toString()?.[0] !== '4') {
        return {}
      }

      // The Rhino is the second buy request on the bundle
      const rhinoFtxSimple = getSimpleForBuyRequest(this.bundleBuyRequest.children[1], this.$rogue)

      // When the rhino functional trainer is selected, the depth can only be 24"
      if (rhinoFtxSimple?.attributes.rig_type === 'Rhino Functional Trainer') {
        return { rack_depth: ['30"', '43"'] }
      }

      return {}
    },
    // Unlike the Rhino case above, this stays selectable (grayedOutValues, not disabledValues) - it's a visual hint only.
    grayedOutConfigAttrs() {
      if (this.bundleBuyRequest.children[0] !== this.buyRequest) {
        return {}
      }

      const mainSimple = this.products[this.buyRequest.children?.[0].productId]
      const bundleProduct = this.products[this.bundleBuyRequest.productId]

      return getSlingerXGrayedOutValues(mainSimple?.attributes.rack_size, bundleProduct?.sku)
    },
    mainBundleProductId() {
      if (!this.attributeOverlapWithMain?.length && !this.product?.attributes?.nameplate_color) {
        return null
      }

      // Hybrid side units carry no rack of their own - children[0] is their nameplate - so the rack comes from the sibling bundle, same as availableOptions.
      return getSimpleForBuyRequest(this.rackBuyRequest ?? this.bundleBuyRequest.children[0], this.$rogue)?.productId || null
    },
  },

  watch: {
    'bundleBuyRequest.productId': {
      immediate: true,
      handler() {
        this.loadOptions()
      },
    },
    buyRequest: {
      immediate : true,
      deep      : true,
      handler() {
        this.loadOptions()
      },
    },
    availableOptions: {
      deep: true,
      handler() {
        if (!this.availableOptions.length) {
          this.buyRequest.qty = 0
          this.selectedOption = ''
          if (this.bundleOption.isRequired) {
            this.$rogue.log.warn(`Required bundle option ${this.bundleOption.title} has not available options`)
          }
        } else if (!this.availableOptions.some(opt => opt.value === this.selectedOption)) {
          if (!this.selectedOption) {
            this.select(this.availableOptions[0].value)
          } else {
            const next = findMostSimilarOption(this.$rogue, this.selectedOption, this.availableOptions)
            this.select(next)
          }
        }
      },
    },
    // When the mainBundleProductId changes, it means we may need to reselect this child
    // (This only fires if both the main product and this product are configurables, and have overlapping options)
    mainBundleProductId: {
      immediate: true,
      handler(newVal, oldVal) {
        // Only if we have selected this bundle option
        if (!this.buyRequest.qty || !newVal) {
          return
        }

        // Make sure the current configurable is still valid
        if (this.attributeOverlapWithMain?.length) {
          // Calling this.select again will force it to pick the best child
          this.select(this.product.productId)

          return
        }

        // Nameplate handling - if we have a nameplate selected, and we pick a new rack color, pick the correct new nameplate
        // This is required only if we allow mismatching nameplates to racks. Otherwise, this code can be deleted.
        if (oldVal && this.product.name?.indexOf('Nameplate') !== -1 && this.product.attributes.nameplate_color) {
          // Figure out what color they changed to
          const newColor = this.products[newVal]?.attributes?.nameplate_color

          // Find the nameplate option that matches the nameplate_color. NOTE: This is not necessarily 1:1 with rig_color
          const newOption = this.availableOptions.filter(opt => !!opt.value).find(opt => {
            const product = this.products[opt.value]

            return product?.name.includes('Nameplate') && product?.attributes.nameplate_color === newColor
          })
          if (newOption) {
            this.select(newOption.value)
          }
        }
      },
    },
  },
  methods: {
    loadOptions() {
      // If the free product is out of stock, you cant select this option
      const forceOOS = this.freeProduct && stockStatus.forProduct(this.freeProduct) !== ''

      this.options = this.bundleOption.values
        .filter(opt => this.products[opt.productId])
        .map(val => {
        // We want to know the price of this bundle option (for text display), and its availability.
        // The most reliable way to do that is to turn it into a buy request and check

          try {
          // @TODO once bundle pricing options are used, we will need to take them into account
            // We may want to use the full bundle buy request, since the universal library will take
            // that into account
            // e.g. opt.pricingType = 'fixed' vs 'discount'
            const optProduct = this.products[val.productId]
            const buyRequest = createBuyRequest(this.products[val.productId], this.$rogue)
            const simple = getSimpleForBuyRequest(buyRequest, this.$rogue)
            const price = computePriceSync(buyRequest, this.$rogue.pinia)
            const status = forceOOS ? 'oos' : stockStatus.forBuyRequest(buyRequest, this.products)
            const img = this.bundleOption.type === 'radio' ? (optProduct.rhpaThumbBundle || optProduct.thumbnail) : undefined

            return {
              value                  : val.productId,
              label                  : `${this.products[val.productId].name}`,
              image                  : img,
              price                  : val.priceType === 'fixed' ? val.price : price,
              status,
              disabled               : status !== '',
              retailPrice            : simple.price,
              showStrikethroughPrice : val.showActualPrice || false,
            }
          } catch (e) {
            this.$rogue.log.warn('Error creating bundle option', e)

            return null
          }
        })
        .filter(o => o !== null)

      // Sometimes the first bundle item has multiple options, which creates a Swatches with the product name
      if (this.availableOptions.length > 1) {
        let prefix = ''
        const split = this.options[0].label.split(' ')
        for (let i = 0; i < split.length; i += 1) {
          const newPrefix = prefix ? `${prefix} ${split[i]}` : split[i]
          if (this.options.some(item => item.label.indexOf(newPrefix) !== 0)) {
            break
          }
          prefix = newPrefix
        }
        // Slinger X: skip stripping if it would empty a sibling's label (e.g. "Seat" vs "Seat + Low Row Kit 2.0")
        const slingerXOptionWouldBeEmpty = this.isSlingerXSide && this.options.some(item => item.label.replace(prefix, '').trim() === '')

        if (prefix.length > 3 && !slingerXOptionWouldBeEmpty) {
          this.options.forEach(item => item.label = item.label.replace(prefix, '').trim())
        }
      }

      // If it's not required, we add a "NONE" swatch / dropdown as the first entry
      if (!this.bundleOption.isRequired) {
        this.options.push({
          price  : 0,
          value  : '',
          status : '',
          label  : 'None',
          image  : '/UI/swatches/none-white_hp9wm5.png',
        })
      }

      // Find the lowest available priced option in this row as a baseline for price
      const minPrice = this.options.slice(0).sort((a, b) => a.price - b.price)[0]?.price

      // Add the (+$100) text to each swatch
      // Selectively hide the price for certain options
      const hideLabelPriceAttrs = this.bundleProduct?.attributes?.hide_bundle_variation_price?.split(',')
      this.options.forEach(o => {
        if (!hideLabelPriceAttrs || !hideLabelPriceAttrs.some(h => o?.label?.toLowerCase()?.includes(h?.trim()?.toLowerCase()))) {
          o.label = this.getOptionLabel(o.label, o.price - minPrice)
        }
      })

      // Add Out of Stock labels to each swatch
      this.options
        .filter(o => o.status !== '')
        .forEach(o => o.label += ' (Out of Stock)')

      // The 'NONE' swatch is the equivalent to setting the buyRequest.qty = 0 (since we can't set
      // the buyRequest.productId = '')
      if (this.buyRequest.productId) {
        this.selectedOption = this.buyRequest.qty === 0 ? '' : this.buyRequest.productId
      }
    },
    getOptionLabel(label, price) {
      if (price) {
        return `${label} (${this.$rogue.currency(price, { plusSymbol: true, euTaxClass: true })})`
      }

      return label
    },
    async select(productId) {
      // We use selectedOption as a proxy - because the "NONE" swatch is not a valid productId
      // When NONE is selected, we just set the qty to 0
      this.selectedOption = productId

      if (productId === '') {
        this.buyRequest.qty = 0
        if (this.freeProductBuyRequest) {
          this.freeProductBuyRequest.qty = 0
        }

        // When a no cable system is selected, add mini feet
        if (this.isTheCave) {
          updateCaveMiniFeetsForCableChange(this.$rogue, this.bundleBuyRequest, this.bundleProduct, this.products, this.inputCode, '')
        }
      } else {
        if (this.isTheCave) { 
          updateCaveMiniFeetsForCableChange(this.$rogue, this.bundleBuyRequest, this.bundleProduct, this.products, this.inputCode, productId)
        }

        // Change the selected product id for this buy request
        if (this.freeProduct && !this.freeProductBuyRequest) {
          // We need to add a buy request for the free product
          const freeBuyRequest = await createBuyRequestAsync(this.freeProduct.productId, this.$rogue)

          // Make sure another call didnt set this already
          if (!this.freeProductBuyRequest) {
            freeBuyRequest.metadata = freeBuyRequest.metadata || {}
            freeBuyRequest.metadata.freeBundleOptionId = this.bundleOption.id
            freeBuyRequest.qty = 0

            this.bundleBuyRequest.children.push(freeBuyRequest)
          }
        }
        // if we are changing configurable options we will confuse the price functionality because we are selecting a
        // config that is different from it's parent. So for the new Id we create a new buyRequest and assign it's
        // children to the current buyRequests children

        let selectedChildId
        if (this.attributeOverlapWithMain) {
          // This means that both the current product, and the main product, are configurables - and have overlapping attributes
          const mainSimple = this.products[this.mainBundleProductId] || { attributes: {} }

          const possibleChildren = this.products[productId].childrenIds
            .map(childId => this.products[childId])
            .filter(child => this.attributeOverlapWithMain.every(code => mainSimple.attributes[code] === child.attributes[code]))

          const currentSimple = getSimpleForBuyRequest(this.buyRequest, this.$rogue)

          const sortByOverlapWithCurrent = possibleChildren.map((child) => ({
            productId   : child.productId,
            attrOverlap : this.product.configurableAttributes
              .filter(attr => child.attributes[attr.code] === currentSimple.attributes[attr.code]).length,
          }))
            .sort((a, b) => b.attrOverlap - a.attrOverlap)

          selectedChildId = sortByOverlapWithCurrent[0]?.productId
        }

        const buyRequest = createBuyRequest(this.products[productId], this.$rogue, selectedChildId)
        if (buyRequest.children?.length) {
          if (!this.buyRequest.children?.length) {
            this.buyRequest.children = buyRequest.children
          } else {
            this.buyRequest.children[0].productId = buyRequest.children[0].productId
          }
        } else if (this.isMassStorage && this.buyRequest.children?.length) {
          // Mass Storage only: clears a stale child left from switching away from the crossmember's rail-mount-hanger sub-configurable (its sole use case).
          this.buyRequest.children = []
        }
        this.buyRequest.productId = buyRequest.productId
        if (!this.buyRequest.qty) {
          this.buyRequest.qty = 1
        }
        if (this.freeProductBuyRequest) {
          this.freeProductBuyRequest.qty = 1
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.bundle-opt {
  margin-bottom: $space-s;
}
</style>
